import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import axios from 'axios';
import { Fragment, useEffect, useState } from 'react';
import { FAQ_CATEGORY_API_URL } from '../../utils/apiUrls';
import { getAccessToken } from '../../utils/token-service';

interface CategoryApiResponse {
	id: number;
	category_name: string;
	step_name: string;
}
interface FaqCategoryDropdownProps {
	onStepNameClick: (id: number) => void;
}
function classNames(...classes: string[]) {
	return classes.filter(Boolean).join(' ');
}

const FaqCategoryDropdown: React.FC<FaqCategoryDropdownProps> = ({ onStepNameClick }) => {
	const [selected, setSelected] = useState<CategoryApiResponse | null>(null);
	const [faqCategory, setFaqCategory] = useState<CategoryApiResponse[]>([]);

	useEffect(() => {
		const fetchData = async () => {
			const accessToken = await getAccessToken();
			const config = {
				method: 'get',
				maxBodyLength: Infinity,
				url: FAQ_CATEGORY_API_URL,
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${accessToken}`,
				},
			};
			try {
				const response = await axios.request<CategoryApiResponse[]>(config);
				setFaqCategory(response.data);
				if (response.data.length > 0) {
					setSelected(response.data[0]);
				}
			} catch (error) {
				console.log('Error calling API:', error);
			}
		};
		fetchData();
	}, []);

	const handleStepNameClick = (id: number | null) => {
		if (id !== null) {
			onStepNameClick(id);
		}
	};

	return (
		<Listbox
			value={selected}
			onChange={(selectedOption) => {
				setSelected(selectedOption);
				handleStepNameClick(selectedOption?.id ?? null);
			}}
		>
			{({ open }) => (
				<>
					<div className="relative mt-2">
						<Listbox.Button className="relative w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6">
							<span className="block truncate">{selected ? selected.step_name : 'Select category'}</span>
							<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
								<ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
							</span>
						</Listbox.Button>
						<Transition
							show={open}
							as={Fragment}
							leave="transition ease-in duration-100"
							leaveFrom="opacity-100"
							leaveTo="opacity-0"
						>
							<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
								{faqCategory.map((category) => (
									<Listbox.Option
										key={category.id}
										className={({ active }) =>
											classNames(
												active ? 'bg-indigo-600 text-white' : 'text-gray-900',
												'relative cursor-default select-none py-2 pl-3 pr-9'
											)
										}
										value={category}
									>
										{({ selected, active }) => (
											<>
												<span className={classNames(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>
													{category.step_name}
												</span>
												{selected ? (
													<span
														className={classNames(
															active ? 'text-white' : 'text-indigo-600',
															'absolute inset-y-0 right-0 flex items-center pr-4'
														)}
													>
														<CheckIcon className="h-5 w-5" aria-hidden="true" />
													</span>
												) : null}
											</>
										)}
									</Listbox.Option>
								))}
							</Listbox.Options>
						</Transition>
					</div>
				</>
			)}
		</Listbox>
	);
};

export default FaqCategoryDropdown;
